import React, { useEffect, useMemo, useRef, useState } from "react";
import { useApp } from "../context/AppContext";
import { useBrand } from "../context/BrandContext";
import api, { eur } from "../lib/api";
import { Button } from "../components/ui/button";
import { Card } from "../components/ui/card";
import { Badge } from "../components/ui/badge";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Textarea } from "../components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { Sheet, SheetContent, SheetHeader, SheetTitle, SheetTrigger, SheetFooter } from "../components/ui/sheet";
import { ShoppingCart, Plus, Minus, Trash2, Search, Package, FileText, CheckCircle2 } from "lucide-react";
import { toast } from "sonner";
import { motion } from "framer-motion";

const errMsg = (e) => e?.response?.data?.detail || "Une erreur est survenue";

function ProductCard({ p, index, inCart, cartSauce, sauces, onAdd, onDec, onSetSauce }) {
  const [sauce, setSauce] = useState(cartSauce || sauces[0] || "");
  const changeSauce = (v) => { setSauce(v); if (inCart > 0) onSetSauce(p, v); };
  return (
    <motion.div initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: Math.min(index * 0.02, 0.3) }}>
      <Card data-testid={`public-product-card-${p.id}`} className="group flex h-full flex-col overflow-hidden transition-all hover:-translate-y-1 hover:shadow-md">
        <div className="relative h-36 overflow-hidden bg-muted">
          {p.image_url ? (
            <img src={p.image_url} alt={p.name} className="h-full w-full object-cover transition-transform duration-500 group-hover:scale-105" />
          ) : (
            <div className="flex h-full w-full items-center justify-center text-muted-foreground/40"><Package className="h-10 w-10" /></div>
          )}
          <Badge className="absolute left-2 top-2 bg-background/90 text-foreground hover:bg-background/90">{p.category}</Badge>
        </div>
        <div className="flex flex-1 flex-col p-4">
          <p className="font-medium leading-tight">{p.name}</p>
          <p className="mt-0.5 text-xs text-muted-foreground">{p.unit}</p>
          <p className="mt-1 font-display text-lg font-bold text-primary">{p.price_on_request ? "Prix sur demande" : eur(p.price)}</p>
          {p.description && <p className="mt-1 text-xs text-muted-foreground line-clamp-2">{p.description}</p>}
          {p.spec_sheet_url && (
            <a href={p.spec_sheet_url} target="_blank" rel="noreferrer" className="mt-1 inline-flex w-fit items-center gap-1 text-xs font-medium text-primary hover:underline">
              <FileText className="h-3 w-3" /> Fiche technique
            </a>
          )}
          {sauces.length > 0 && (
            <div className="mt-3">
              <label className="mb-1 block text-xs font-medium text-muted-foreground">Sauce</label>
              <Select value={sauce} onValueChange={changeSauce}>
                <SelectTrigger className="h-9"><SelectValue placeholder="Choisir une sauce" /></SelectTrigger>
                <SelectContent>{sauces.map((s) => <SelectItem key={s} value={s}>{s}</SelectItem>)}</SelectContent>
              </Select>
            </div>
          )}
          <div className="mt-3 flex items-center justify-end">
            {inCart > 0 ? (
              <div className="flex items-center gap-2">
                <Button size="icon" variant="outline" className="h-8 w-8" onClick={() => onDec(p)}><Minus className="h-4 w-4" /></Button>
                <span className="tabular w-6 text-center font-semibold">{inCart}</span>
                <Button size="icon" className="h-8 w-8" onClick={() => onAdd(p, 1, sauce)}><Plus className="h-4 w-4" /></Button>
              </div>
            ) : (
              <Button size="sm" data-testid={`public-add-${p.id}`} onClick={() => onAdd(p, 1, sauce)}><Plus className="mr-1 h-4 w-4" /> Ajouter</Button>
            )}
          </div>
        </div>
      </Card>
    </motion.div>
  );
}

export default function PublicCatalogView() {
  const brand = useBrand();
  const sauces = brand.sauces;
  const { cart, addToCart, setQuantity, setSauce, removeFromCart, clearCart, cartCount } = useApp();
  const [products, setProducts] = useState([]);
  const [query, setQuery] = useState("");
  const [category, setCategory] = useState("Tous");
  const [cartOpen, setCartOpen] = useState(false);
  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [phone, setPhone] = useState("");
  const [notes, setNotes] = useState("");
  const [placing, setPlacing] = useState(false);
  const [confirmed, setConfirmed] = useState(null);

  useEffect(() => { api.get("/products").then((r) => setProducts(r.data)).catch(() => {}); }, []);

  // Lien « Recommander la même chose » de l'email de confirmation : ?reorder=<jeton>
  const reorderDone = useRef(false);
  useEffect(() => {
    const token = new URLSearchParams(window.location.search).get("reorder");
    if (!token || !products.length || reorderDone.current) return;
    reorderDone.current = true;
    window.history.replaceState(null, "", window.location.pathname);
    api.get(`/reorder/${encodeURIComponent(token)}`).then(({ data }) => {
      const byId = Object.fromEntries(products.map((p) => [p.id, p]));
      let missing = 0;
      clearCart();
      data.items.forEach((it) => {
        const p = byId[it.product_id];
        if (p) addToCart(p, it.quantity, it.sauce && it.sauce !== "—" ? it.sauce : undefined);
        else missing += 1;
      });
      setName(data.name || "");
      setEmail(data.email || "");
      setPhone(data.phone || "");
      setCartOpen(true);
      toast.success(`Votre commande ${data.order_number} est dans le panier`, {
        description: missing
          ? `${missing} produit(s) ne sont plus au catalogue. Vérifiez le panier avant de valider.`
          : "Vérifiez les quantités puis validez.",
      });
    }).catch(() => toast.error("Ce lien a expiré. Composez votre commande dans le catalogue."));
  }, [products, addToCart, clearCart]);

  const categories = useMemo(() => ["Tous", ...new Set(products.map((p) => p.category).filter(Boolean))], [products]);
  const filtered = useMemo(() => products.filter((p) =>
    (category === "Tous" || p.category === category) &&
    (!query || p.name.toLowerCase().includes(query.toLowerCase()))
  ), [products, category, query]);

  const cartItems = Object.values(cart);
  const cartTotal = cartItems.reduce((s, i) => s + (i.product.price_on_request ? 0 : i.product.price * i.quantity), 0);
  const cartQuote = cartItems.some((i) => i.product.price_on_request);

  const placeOrder = async () => {
    if (!name.trim() || !email.trim()) { toast.error("Nom et email requis"); return; }
    const items = cartItems.map((i) => ({ product_id: i.product.id, quantity: i.quantity, sauce: i.sauce || sauces[0] || "" }));
    if (!items.length) { toast.error("Votre panier est vide"); return; }
    setPlacing(true);
    try {
      const { data } = await api.post("/orders", { guest_name: name.trim(), guest_email: email.trim(), guest_phone: phone.trim(), items, notes });
      setConfirmed(data.order_number);
      clearCart();
      setCartOpen(false);
      toast.success(`Commande ${data.order_number} envoyée`);
    } catch (e) {
      toast.error(errMsg(e));
    } finally {
      setPlacing(false);
    }
  };

  return (
    <div data-testid="public-catalog-view">
      <div className="mb-6 flex flex-wrap items-center justify-between gap-3">
        <div>
          <h1 className="font-display text-2xl font-extrabold tracking-tight">Catalogue</h1>
          <p className="text-sm text-muted-foreground">Parcourez nos produits, ajoutez-les au panier, puis passez votre commande.</p>
        </div>
        <Sheet open={cartOpen} onOpenChange={setCartOpen}>
          <SheetTrigger asChild>
            <Button variant="outline" className="gap-2" data-testid="public-cart-btn">
              <ShoppingCart className="h-4 w-4" /> Panier {cartCount > 0 && <Badge className="ml-1">{cartCount}</Badge>}
            </Button>
          </SheetTrigger>
          <SheetContent className="flex w-full flex-col sm:max-w-md">
            <SheetHeader><SheetTitle>Votre panier</SheetTitle></SheetHeader>
            <div className="flex-1 overflow-y-auto py-4">
              {cartItems.length === 0 ? (
                <p className="text-center text-sm text-muted-foreground">Panier vide.</p>
              ) : (
                <div className="space-y-3">
                  {cartItems.map(({ product, quantity, sauce }) => (
                    <div key={product.id} className="flex items-start justify-between gap-2 rounded-md border border-border p-3" data-testid={`public-cart-item-${product.id}`}>
                      <div className="flex-1">
                        <p className="text-sm font-medium">{product.name}</p>
                        <p className="text-xs text-muted-foreground">{product.price_on_request ? "Prix sur demande" : eur(product.price)} × {quantity}</p>
                        {sauces.length > 0 && <p className="text-xs text-muted-foreground">Sauce : {sauce || sauces[0]}</p>}
                      </div>
                      <div className="flex items-center gap-1.5">
                        <Button size="icon" variant="outline" className="h-7 w-7" onClick={() => setQuantity(product, quantity - 1)}><Minus className="h-3.5 w-3.5" /></Button>
                        <span className="tabular w-5 text-center text-sm">{quantity}</span>
                        <Button size="icon" variant="outline" className="h-7 w-7" onClick={() => setQuantity(product, quantity + 1)}><Plus className="h-3.5 w-3.5" /></Button>
                        <Button size="icon" variant="ghost" className="h-7 w-7 text-destructive" onClick={() => removeFromCart(product.id)}><Trash2 className="h-3.5 w-3.5" /></Button>
                      </div>
                    </div>
                  ))}
                  <div className="border-t border-border pt-3">
                    <Label>Votre nom</Label>
                    <Input data-testid="public-guest-name" value={name} onChange={(e) => setName(e.target.value)} placeholder="Nom / société" />
                  </div>
                  <div>
                    <Label>Email</Label>
                    <Input data-testid="public-guest-email" type="email" value={email} onChange={(e) => setEmail(e.target.value)} placeholder="vous@exemple.fr" />
                  </div>
                  <div>
                    <Label>Téléphone (facultatif)</Label>
                    <Input data-testid="public-guest-phone" value={phone} onChange={(e) => setPhone(e.target.value)} />
                  </div>
                  <div>
                    <Label>Notes (facultatif)</Label>
                    <Textarea data-testid="public-guest-notes" value={notes} onChange={(e) => setNotes(e.target.value)} rows={2} />
                  </div>
                </div>
              )}
            </div>
            {cartItems.length > 0 && (
              <SheetFooter className="flex-col gap-2 sm:flex-col">
                <div className="flex w-full items-center justify-between text-lg font-bold">
                  <span>{cartQuote ? "Total (hors devis)" : "Total"}</span><span className="tabular">{eur(cartTotal)}</span>
                </div>
                {cartQuote && (
                  <p className="w-full text-xs text-muted-foreground" data-testid="public-quote-note">
                    Les articles « prix sur demande » vous seront chiffrés par email avant toute préparation.
                  </p>
                )}
                <Button className="w-full" size="lg" onClick={placeOrder} disabled={placing} data-testid="public-place-order-btn">
                  {placing ? "Envoi..." : cartQuote ? "Envoyer ma demande" : "Passer commande"}
                </Button>
              </SheetFooter>
            )}
          </SheetContent>
        </Sheet>
      </div>

      {confirmed && (
        <Card className="mb-6 flex items-center gap-3 border-primary/40 bg-primary/5 p-4" data-testid="public-order-confirmed">
          <CheckCircle2 className="h-6 w-6 text-primary" />
          <div>
            <p className="font-semibold">Commande {confirmed} envoyée</p>
            <p className="text-sm text-muted-foreground">Vous recevrez une confirmation par email. Nous vous recontacterons pour la suite.</p>
          </div>
        </Card>
      )}

      <div className="mb-5 flex flex-wrap items-center gap-3">
        <div className="relative flex-1 min-w-[200px]">
          <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
          <Input className="pl-9" placeholder="Rechercher un produit..." value={query} onChange={(e) => setQuery(e.target.value)} data-testid="public-search-input" />
        </div>
        <Select value={category} onValueChange={setCategory}>
          <SelectTrigger className="w-52" data-testid="public-category-select"><SelectValue /></SelectTrigger>
          <SelectContent>{categories.map((c) => <SelectItem key={c} value={c}>{c}</SelectItem>)}</SelectContent>
        </Select>
      </div>

      <div className="grid grid-cols-1 gap-5 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4">
        {filtered.map((p, i) => (
          <ProductCard
            key={p.id} p={p} index={i} sauces={sauces}
            inCart={cart[p.id]?.quantity || 0}
            cartSauce={cart[p.id]?.sauce}
            onAdd={addToCart} onDec={(prod) => setQuantity(prod, (cart[prod.id]?.quantity || 1) - 1)}
            onSetSauce={setSauce}
          />
        ))}
        {filtered.length === 0 && (
          <p className="col-span-full py-10 text-center text-muted-foreground">Aucun produit.</p>
        )}
      </div>
    </div>
  );
}
